import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { WorkflowRun } from "@/stores/workflowRunsStore";
import type { PaginatedData } from "@/types/pagination";

export const DEFAULT_WORKFLOW_RUNS_PAGE_SIZE = 10;
export const MAX_WORKFLOW_RUNS_PAGE_SIZE = 100;

export interface WorkflowRunsApiResponse {
  code: number;
  message: string;
  data: PaginatedData<WorkflowRun>;
  requestId: string;
}

interface WorkflowRunByIdApiResponse {
  code: number;
  message?: string;
  data: WorkflowRun;
}

interface FetchWorkflowRunsPageOptions {
  workflowId?: string;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
  status?: string;
}

interface WorkflowRunTimeRange {
  startedAt?: string;
  finishedAt?: string;
}

export async function fetchWorkflowRunsPage({
  workflowId,
  limit = DEFAULT_WORKFLOW_RUNS_PAGE_SIZE,
  offset = 0,
  signal,
  status,
}: FetchWorkflowRunsPageOptions): Promise<PaginatedData<WorkflowRun>> {
  const params = new URLSearchParams();
  params.append("limit", String(Math.min(limit, MAX_WORKFLOW_RUNS_PAGE_SIZE)));
  params.append("offset", String(offset));
  if (status) {
    params.append("status", status);
  }

  const baseUrl = workflowId ? `${V2_ENDPOINT}/v1/workflows/${workflowId}/runs` : `${V2_ENDPOINT}/v1/runs`;
  const response = await authenticatedFetch<WorkflowRunsApiResponse>(`${baseUrl}?${params.toString()}`, { signal });

  if (!response.ok) {
    throw new Error(`Failed to fetch workflow runs: ${response.statusText}`);
  }

  const data = await response.json();
  if (data.code !== 0) {
    throw new Error(data.message || "Failed to fetch workflow runs");
  }

  return data.data;
}

export async function fetchWorkflowRunById(runId: string): Promise<WorkflowRun> {
  const response = await authenticatedFetch<WorkflowRunByIdApiResponse>(`${V2_ENDPOINT}/v1/runs/${runId}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch run: ${response.statusText}`);
  }

  const data = await response.json();
  if (data.code !== 0) {
    throw new Error(data.message || "Failed to fetch workflow run");
  }

  return data.data;
}

// NOTE: we intentionally parse both strings via `new Date()` rather than
// `localeCompare` because Go's `time.Time.MarshalJSON` emits RFC3339Nano, which
// TRIMS trailing zeros from the fractional-seconds field. That means two runs
// in the same second can serialize to different lengths (e.g. `…:00Z` vs
// `…:00.1Z`) and lex compare would reverse them ('.' < 'Z').
export function compareWorkflowRunsByStartedAtDesc(a: WorkflowRun, b: WorkflowRun): number {
  return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
}

export function compareWorkflowRunsByStartedAtAsc(a: WorkflowRun, b: WorkflowRun): number {
  return new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime();
}

export function sortWorkflowRunsByStartedAtDesc(runs: WorkflowRun[]): WorkflowRun[] {
  return [...runs].sort(compareWorkflowRunsByStartedAtDesc);
}

export function sortWorkflowRunsByStartedAtAsc(runs: WorkflowRun[]): WorkflowRun[] {
  return [...runs].sort(compareWorkflowRunsByStartedAtAsc);
}

export function getWorkflowRunDurationMs({ startedAt, finishedAt }: WorkflowRunTimeRange): number | undefined {
  if (!startedAt || !finishedAt) return undefined;
  return new Date(finishedAt).getTime() - new Date(startedAt).getTime();
}

export function compareWorkflowRunsByDuration(a: WorkflowRun, b: WorkflowRun): number {
  return (getWorkflowRunDurationMs(a) || 0) - (getWorkflowRunDurationMs(b) || 0);
}

// Severity order — actionable states first so "Sort by Status" surfaces them.
// Partial runs sit between failure and running: less urgent than a hard
// failure, but still worth surfacing above clean successes.
const STATUS_SORT_ORDER: Record<WorkflowRun["status"], number> = {
  failure: 0,
  partial: 1,
  running: 2,
  waiting: 3,
  pending: 4,
  success: 5,
  cancelled: 6,
};

export interface WorkflowRunQueryOptions {
  workflowId?: string;
  status?: WorkflowRun["status"];
  searchTerm?: string;
  sortBy?: "startedAt" | "duration" | "status";
  sortOrder?: "asc" | "desc";
  limit?: number;
}

/** Pure filter + sort + limit over runs. Wrap in `useMemo` keyed on `runs` + option primitives. */
export function filterWorkflowRuns(
  runs: WorkflowRun[],
  options?: WorkflowRunQueryOptions,
  getWorkflowName?: (run: WorkflowRun) => string,
): WorkflowRun[] {
  if (!options) return runs;

  let filtered: WorkflowRun[] = runs;

  if (options.workflowId) {
    filtered = filtered.filter(r => r.workflowId === options.workflowId);
  }

  if (options.status) {
    filtered = filtered.filter(r => r.status === options.status);
  }

  if (options.searchTerm) {
    const searchLower = options.searchTerm.toLowerCase();
    filtered = filtered.filter(r => {
      const matchesId = r.id.toLowerCase().includes(searchLower) || r.workflowId.toLowerCase().includes(searchLower);
      if (getWorkflowName) {
        const workflowName = getWorkflowName(r).toLowerCase();
        return matchesId || workflowName.includes(searchLower);
      }
      return matchesId;
    });
  }

  if (options.sortBy) {
    const order = options.sortOrder === "desc" ? -1 : 1;
    const baseCmp: (a: WorkflowRun, b: WorkflowRun) => number =
      options.sortBy === "startedAt"
        ? compareWorkflowRunsByStartedAtAsc
        : options.sortBy === "duration"
          ? compareWorkflowRunsByDuration
          : options.sortBy === "status"
            ? (a, b) => STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status]
            : () => 0;
    // Copy before sort — don't mutate the input array (or the original `runs` ref
    // from Zustand, which other subscribers may still be iterating).
    filtered = [...filtered].sort((a, b) => baseCmp(a, b) * order);
  }

  if (options.limit) {
    filtered = filtered.slice(0, options.limit);
  }

  return filtered;
}
